/**
 * Connectivity repair.
 *
 * Every generator carves optimistically — corridors punch through rooms, halls
 * overwrite doorways, warrens rebuild whole leaves — so the result is allowed
 * to strand pockets of floor. This pass is the guarantee that the rest of the
 * game can rely on: after it runs, every walkable cell is reachable from every
 * other one. Small pockets are filled in, larger ones are tunnelled to.
 */

import { Cell, type Grid } from '../../core/types';

/** Pockets with fewer cells than this are filled in rather than joined. */
const MIN_POCKET = 6;

const DX = [1, -1, 0, 0];
const DY = [0, 0, 1, -1];

/**
 * Make the whole level one connected component. Returns the number of pockets
 * that had to be tunnelled to, which is only interesting while tuning.
 */
export function repairConnectivity(grid: Grid): number {
  const { w, h } = grid;
  const n = w * h;
  const comp = new Int32Array(n).fill(-1);
  const queue = new Int32Array(n);
  const sizes: number[] = [];

  // Label components of walkable cells.
  let compCount = 0;
  for (let seed = 0; seed < n; seed++) {
    if (comp[seed] !== -1) continue;
    const sc = grid.cells[seed]!;
    if (sc === Cell.Void || sc === Cell.Wall) continue;
    let head = 0;
    let tail = 0;
    comp[seed] = compCount;
    queue[tail++] = seed;
    let size = 0;
    while (head < tail) {
      const idx = queue[head++]!;
      size++;
      const cx = idx % w;
      const cy = (idx / w) | 0;
      for (let d = 0; d < 4; d++) {
        const nx = cx + DX[d]!;
        const ny = cy + DY[d]!;
        if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
        const nIdx = ny * w + nx;
        if (comp[nIdx] !== -1) continue;
        const c = grid.cells[nIdx]!;
        if (c === Cell.Void || c === Cell.Wall) continue;
        comp[nIdx] = compCount;
        queue[tail++] = nIdx;
      }
    }
    sizes.push(size);
    compCount++;
  }
  if (compCount <= 1) return 0;

  let main = 0;
  for (let c = 1; c < compCount; c++) if (sizes[c]! > sizes[main]!) main = c;

  // Wipe the specks. They are usually a single cell left over from an overlay.
  for (let i = 0; i < n; i++) {
    const c = comp[i]!;
    if (c >= 0 && c !== main && sizes[c]! < MIN_POCKET) {
      grid.cells[i] = Cell.Wall;
      comp[i] = -1;
    }
  }

  // BFS out of the main component through *everything* (walls included) so we
  // can walk the parent chain back from a stranded pocket along the shortest
  // tunnel. The outer ring is excluded so a tunnel can never breach the map.
  const dist = new Int32Array(n).fill(-1);
  const parent = new Int32Array(n).fill(-1);
  let head = 0;
  let tail = 0;
  for (let i = 0; i < n; i++) {
    if (comp[i] === main) {
      dist[i] = 0;
      queue[tail++] = i;
    }
  }
  while (head < tail) {
    const idx = queue[head++]!;
    const cx = idx % w;
    const cy = (idx / w) | 0;
    const nd = dist[idx]! + 1;
    for (let d = 0; d < 4; d++) {
      const nx = cx + DX[d]!;
      const ny = cy + DY[d]!;
      if (nx < 1 || ny < 1 || nx >= w - 1 || ny >= h - 1) continue;
      const nIdx = ny * w + nx;
      if (dist[nIdx] !== -1) continue;
      dist[nIdx] = nd;
      parent[nIdx] = idx;
      queue[tail++] = nIdx;
    }
  }

  // Cheapest entry point into each surviving pocket.
  const best = new Int32Array(compCount).fill(-1);
  for (let i = 0; i < n; i++) {
    const c = comp[i]!;
    if (c < 0 || c === main) continue;
    if (dist[i] < 0) continue;
    const b = best[c]!;
    if (b < 0 || dist[i]! < dist[b]!) best[c] = i;
  }

  let tunnels = 0;
  const path: number[] = [];
  for (let c = 0; c < compCount; c++) {
    if (c === main || sizes[c]! < MIN_POCKET) continue;
    const start = best[c]!;
    if (start < 0) {
      // Unreachable even through solid rock: it must be outside the interior.
      for (let i = 0; i < n; i++) if (comp[i] === c) grid.cells[i] = Cell.Wall;
      continue;
    }
    path.length = 0;
    for (let idx = start; idx >= 0 && dist[idx]! > 0; idx = parent[idx]!) path.push(idx);
    for (const idx of path) {
      const cell = grid.cells[idx]!;
      if (cell === Cell.Void || cell === Cell.Wall) grid.cells[idx] = Cell.Floor;
    }
    // A single carved cell with solid to either side reads as a doorway.
    for (const idx of path) {
      if (grid.cells[idx] !== Cell.Floor) continue;
      const cx = idx % w;
      const cy = (idx / w) | 0;
      const horiz = grid.isSolid(cx, cy - 1) && grid.isSolid(cx, cy + 1);
      const vert = grid.isSolid(cx - 1, cy) && grid.isSolid(cx + 1, cy);
      if (horiz !== vert) grid.cells[idx] = Cell.Doorway;
    }
    tunnels++;
  }
  return tunnels;
}

/**
 * Final safety net: anything the spawn BFS could not reach is turned back into
 * wall, so no system can ever hand the player an unreachable target.
 */
export function sealUnreachable(grid: Grid, distanceFromSpawn: Uint16Array, unreachable: number): number {
  let sealed = 0;
  for (let i = 0; i < grid.cells.length; i++) {
    const c = grid.cells[i]!;
    if (c === Cell.Void || c === Cell.Wall) continue;
    if (distanceFromSpawn[i] !== unreachable) continue;
    grid.cells[i] = Cell.Wall;
    sealed++;
  }
  return sealed;
}
